import { motion } from "framer-motion";
import { Asterisk, BadgeCheck, Wallet } from "lucide-react";
import { EASE } from "./Reveal";

const HERO_IMG =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_edualert-partner/artifacts/panr16rw_final.png";

const AUDIENCES = [
  "Teachers",
  "Counsellors",
  "Mentors",
  "Tutors",
  "Education Creators",
  "Influencers",
  "Telegram/WhatsApp Group Admins",
  "Students / Recent Graduates",
];

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function HeroSection() {
  return (
    <section data-testid="hero-section" className="relative isolate">
      <div className="pointer-events-none absolute -right-32 top-24 -z-10 h-96 w-96 rounded-full bg-[#e8eaf6] blur-3xl" />
      <div className="mx-auto max-w-6xl px-4 pt-28 sm:px-6 sm:pt-36 lg:px-8">
        <div className="grid items-end gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="mb-6 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.24em] text-[#f57c00]">
              <span className="h-2 w-2 rounded-full bg-[#f57c00]" />
              <span className="font-mono-edu">EduAlert Partner Program</span>
            </p>
            <h1 className="font-display text-4xl font-black leading-[1.08] tracking-tight text-[#1a237e] sm:text-5xl lg:text-[3.5rem]">
              <MaskedLine delay={0.05}>Are You Connected</MaskedLine>
              <MaskedLine delay={0.16}>With Students of</MaskedLine>
              <MaskedLine delay={0.27}>
                <span className="relative inline-block px-1">
                  <span className="absolute inset-x-0 bottom-1 top-[52%] -rotate-1 rounded-sm bg-[#f57c00]/20" />
                  <span className="relative">Class 6 to 12?</span>
                </span>
              </MaskedLine>
            </h1>
            <motion.p
              className="mt-6 max-w-xl text-lg font-medium leading-relaxed text-slate-600 sm:text-xl"
              initial={{ y: 24 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.8, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              Turn Your Network Into an Opportunity with EduAlert Partner
              Program!
            </motion.p>
          </div>

          <motion.div
            className="hidden max-w-md items-start gap-3 border-l-[3px] border-[#f57c00] pl-4 text-sm leading-relaxed text-slate-700 lg:col-span-5 lg:flex lg:pb-2"
            initial={{ y: 24 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            If you guide, teach, support students of Class 6 to 12, or have a
            student audience — this is for you!
          </motion.div>
        </div>

        <motion.div
          className="relative mt-10 overflow-hidden rounded-[2rem] border border-[#e2e8f0] shadow-2xl shadow-[#1a237e]/20"
          initial={{ opacity: 0, scale: 0.97, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
        >
          <img
            src={HERO_IMG}
            alt="A mentor shaking hands with a teacher while students celebrate updates on the EduAlert app"
            data-testid="hero-image"
            className="h-auto w-full"
          />
          <div className="hidden h-24 bg-gradient-to-t from-[#0d1b60]/60 to-transparent sm:block" />
          <div className="flex flex-wrap gap-3 p-4 sm:absolute sm:bottom-4 sm:left-4 sm:p-0">
            <span
              data-testid="hero-stat-partner"
              className="flex items-center gap-2 rounded-2xl border border-white/40 bg-white/85 px-4 py-2.5 text-sm font-bold text-[#1a237e] backdrop-blur-md"
            >
              <BadgeCheck className="h-4 w-4 text-[#f57c00]" />
              ₹150 per referral
            </span>
            <span
              data-testid="hero-stat-student"
              className="flex items-center gap-2 rounded-2xl border border-white/40 bg-white/85 px-4 py-2.5 text-sm font-bold text-[#1a237e] backdrop-blur-md"
            >
              <Wallet className="h-4 w-4 text-[#f57c00]" />
              ₹50 student cashback
            </span>
          </div>
        </motion.div>

        <motion.p
          className="mt-10 max-w-xl border-l-[3px] border-[#f57c00] pl-4 text-sm leading-relaxed text-slate-700 lg:hidden"
          initial={{ y: 20 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          If you guide, teach, support students of Class 6 to 12, or have a
          student audience — this is for you!
        </motion.p>
      </div>

      <div
        data-testid="audience-marquee"
        className="mt-12 overflow-hidden border-y border-[#e2e8f0] bg-white py-5"
      >
        <div className="animate-marquee flex w-max">
          {[0, 1].map((copy) => (
            <div
              key={copy}
              aria-hidden={copy === 1}
              className="flex shrink-0 items-center"
            >
              {AUDIENCES.map((audience) => (
                <span
                  key={`${copy}-${audience}`}
                  className="mx-7 flex items-center gap-3 whitespace-nowrap text-sm font-bold uppercase tracking-[0.16em] text-[#1a237e]"
                >
                  <Asterisk className="h-4 w-4 text-[#f57c00]" />
                  {audience}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}